export const YOUTUBE_NOCOOKIE_ORIGIN = "https://www.youtube-nocookie.com";

export type YoutubePlayerInfo = {
  playerState?: number;
  muted?: boolean;
  volume?: number;
  currentTime?: number;
  duration?: number;
};

type YoutubeMessage = {
  event?: string;
  info?: YoutubePlayerInfo;
};

/**
 * O iframe nasce como about:blank (origem da página). Mandar postMessage com
 * target youtube-nocookie nessa hora gera o aviso de origem e, se o widget
 * já abriu pela metade, o crash `isExternalMethodAvailable is not a function`.
 */
export function isYoutubeIframeNavigated(frame: HTMLIFrameElement | null): boolean {
  const win = frame?.contentWindow;
  if (!win) return false;
  try {
    void win.location.href;
    return false;
  } catch {
    return true;
  }
}

export function postToYoutubeIframe(
  frame: HTMLIFrameElement | null,
  payload: Record<string, unknown>,
): boolean {
  if (!isYoutubeIframeNavigated(frame) || !frame?.contentWindow) return false;
  frame.contentWindow.postMessage(JSON.stringify(payload), YOUTUBE_NOCOOKIE_ORIGIN);
  return true;
}

export function postYoutubeCommand(
  frame: HTMLIFrameElement | null,
  func: string,
  args: unknown[] = [],
): boolean {
  return postToYoutubeIframe(frame, { event: "command", func, args });
}

export function postYoutubeListening(frame: HTMLIFrameElement | null, id: string): boolean {
  return postToYoutubeIframe(frame, { event: "listening", id });
}

export function isYoutubePlayerReadyEvent(eventName: string | undefined): boolean {
  return eventName === "onReady" || eventName === "initialDelivery";
}

export function readYoutubeMessage(
  event: MessageEvent,
  frame: HTMLIFrameElement | null,
): YoutubeMessage | null {
  if (event.origin !== YOUTUBE_NOCOOKIE_ORIGIN) return null;
  if (!frame || event.source !== frame.contentWindow) return null;

  if (typeof event.data === "string") {
    try {
      return JSON.parse(event.data) as YoutubeMessage;
    } catch {
      return null;
    }
  }
  if (event.data && typeof event.data === "object") {
    return event.data as YoutubeMessage;
  }
  return null;
}
